// <scroll-carousel>: arrow buttons ([data-dir]) scroll the [data-track] by one card, or
// by data-cards-per-move cards where a section asks for more.
//
// The arrows are not decoration — the rails set .scrollbar-none, so on a touch device
// they and the peeking next card are the ONLY cues that a rail scrolls. That makes their
// state part of the affordance: they hide themselves when there is nothing to scroll (an
// arrow that does nothing teaches the opposite lesson) and disable at each end.
//
// State is re-derived rather than measured once at connect, because several rails get
// their cards after a fetch: product recommendations swap in a rendered section, and
// Recently viewed appends cards one request at a time.
customElements.define(
  "scroll-carousel",
  class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-track]");
      if (!this.track) return;
      this.arrows = Array.from(this.querySelectorAll("[data-dir]"));
      this.sync = this.sync.bind(this);

      this.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-dir]");
        if (!btn) return;
        const first = this.track.firstElementChild;
        const stride = first ? first.getBoundingClientRect().width + this.gap() : 330;
        const step = stride * this.cardsPerMove(stride);
        this.track.scrollBy({ left: btn.dataset.dir === "next" ? step : -step, behavior: "smooth" });
      });

      this.track.addEventListener("scroll", this.sync, { passive: true });
      // Resize catches a viewport change or a card image settling; childList catches cards
      // being appended, which need not change the track's own box.
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.sync);
        this.ro.observe(this.track);
      }
      this.mo = new MutationObserver(this.sync);
      this.mo.observe(this.track, { childList: true });
      this.sync();
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.mo) this.mo.disconnect();
    }

    // Real gap, not a hardcoded 14: the rails use 14px, 20px and a computed value.
    gap() {
      return parseFloat(getComputedStyle(this.track).columnGap) || 14;
    }

    // Cards moved per arrow press. Opt-in via data-cards-per-move (Category carousel
    // exposes it as a merchant setting); every other rail keeps the one-card default.
    //
    // Clamped to the cards actually on screen, because the count that reads as "one
    // page" on desktop is more than a phone shows: without the clamp a press on a rail
    // peeking 1.2 cards would scroll past a card nobody ever saw. n cards occupy
    // n * stride - gap, so n fits while n <= (clientWidth + gap) / stride.
    cardsPerMove(stride) {
      const want = Math.max(1, parseInt(this.dataset.cardsPerMove, 10) || 1);
      if (want === 1 || !stride) return 1;
      const fits = Math.floor((this.track.clientWidth + this.gap()) / stride);
      return Math.max(1, Math.min(want, fits));
    }

    sync() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      // 2px of slack: sub-pixel track widths otherwise leave a rail permanently "1px
      // scrollable", which would show two dead arrows on a rail that clearly fits.
      const scrollable = max > 2;
      const atStart = t.scrollLeft <= 2;
      const atEnd = t.scrollLeft >= max - 2;
      this.arrows.forEach((b) => {
        // The `hidden` attribute rather than a class: [hidden] is !important in
        // tailwind.css, so it beats the display utilities the arrows carry, and clearing
        // it hands visibility back to them (showcase-carousel hides its arrows below md).
        b.hidden = !scrollable;
        b.disabled = b.dataset.dir === "next" ? atEnd : atStart;
      });
    }
  }
);
